import { ENV } from '../config/env';

declare global {
  interface Window {
    OneSignalDeferred?: Array<(sdk: any) => void>;
    OneSignal?: any;
  }
}

type DeferredCallback = (sdk: any) => void | Promise<void>;

const ensureDeferredQueue = () => {
  if (!window.OneSignalDeferred) {
    window.OneSignalDeferred = [];
  }
};

export class OneSignalService {
  private static instance: OneSignalService;
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  static getInstance(): OneSignalService {
    if (!OneSignalService.instance) {
      OneSignalService.instance = new OneSignalService();
    }
    return OneSignalService.instance;
  }

  private enqueue(callback: DeferredCallback) {
    ensureDeferredQueue();
    window.OneSignalDeferred!.push(async (OneSignal) => {
      if (typeof callback === 'function') {
        await callback(OneSignal);
      }
    });
  }

  initialize(): Promise<void> {
    if (!ENV.ONESIGNAL_APP_ID) {
      console.warn('OneSignal App ID is not configured. Push notifications will be disabled.');
      return Promise.resolve();
    }

    if (this.initialized) {
      return Promise.resolve();
    }

    if (!this.initPromise) {
      this.initPromise = new Promise<void>((resolve, reject) => {
        this.enqueue(async (OneSignal) => {
          try {
            await OneSignal.init({
              appId: ENV.ONESIGNAL_APP_ID,
              safari_web_id: ENV.ONESIGNAL_SAFARI_WEB_ID || undefined,
              notifyButton: {
                enable: true,
              },
            });
            this.initialized = true;
            resolve();
          } catch (error) {
            console.error('Failed to initialize OneSignal:', error);
            reject(error);
          }
        });
      });
    }

    return this.initPromise;
  }

  async login(userId: string): Promise<void> {
    if (!userId) return;
    await this.initialize();
    return new Promise((resolve) => {
      this.enqueue(async (OneSignal) => {
        try {
          await OneSignal.login(userId);
        } catch (error) {
          console.warn('OneSignal login failed:', error);
        } finally {
          resolve();
        }
      });
    });
  }

  async logout(): Promise<void> {
    if (!this.initialized) return;
    return new Promise((resolve) => {
      this.enqueue(async (OneSignal) => {
        try {
          await OneSignal.logout();
        } catch (error) {
          console.warn('OneSignal logout failed:', error);
        } finally {
          resolve();
        }
      });
    });
  }

  async requestPermission(): Promise<NotificationPermission | 'default'> {
    if (!ENV.ONESIGNAL_APP_ID) {
      return Notification.permission;
    }

    await this.initialize();

    return new Promise((resolve) => {
      this.enqueue(async (OneSignal) => {
        try {
          const state = await OneSignal.Notifications.getPermissionStatus();
          if (state === 'granted') {
            resolve('granted');
            return;
          }

          const result = await OneSignal.Notifications.requestPermission();
          resolve(result ? 'granted' : Notification.permission);
        } catch (error) {
          console.warn('OneSignal permission request failed:', error);
          resolve(Notification.permission);
        }
      });
    });
  }

  async getPushSubscriptionId(): Promise<string | null> {
    if (!this.initialized) return null;

    return new Promise((resolve) => {
      this.enqueue(async (OneSignal) => {
        try {
          const subscription = await OneSignal.User.PushSubscription.getId();
          resolve(subscription || null);
        } catch (error) {
          console.warn('Failed to get OneSignal subscription ID:', error);
          resolve(null);
        }
      });
    });
  }

  async onForegroundNotification(callback: (event: any) => void): Promise<void> {
    if (!callback) return;
    await this.initialize();

    this.enqueue((OneSignal) => {
      OneSignal.Notifications.addEventListener('foregroundWillDisplay', callback);
    });
  }

  async onNotificationClick(callback: (event: any) => void): Promise<void> {
    if (!callback) return;
    await this.initialize();

    this.enqueue((OneSignal) => {
      OneSignal.Notifications.addEventListener('click', callback);
    });
  }
}

